<script setup lang="ts">
import type { IPromoProduct } from "@magnit/unit-catalog/src/types";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import { computed } from "vue";
import {
  pluralRatingCount,
  pluralScoreCount,
} from "@magnit/unit-catalog/src/utils/plural";
import StarIcon from "@magnit/icons/src/assets/svg/icons/base/16-system-star.svg";
import ArrowIcon from "@magnit/icons/src/assets/icons/arrow-right-corner-small-24.svg";
import type { AnalyticsEventArguments } from "../types/analytics";
import type { ICatalogBFFDetailedGood } from "../types/goods";
import { collectReviewPayload } from "../utils/analytics";

const props = defineProps<{
  product: ICatalogBFFDetailedGood | IPromoProduct;
  categoryName: string;
  categoryId?: string;
  theme: "base" | "V1";
}>();
const emit = defineEmits<{
  analytics: AnalyticsEventArguments;
}>();
const ratings = computed(() => {
  return props.product.ratings;
});
const commentsCount = computed(() =>
  ratings.value?.commentsCount
    ? `· ${props.product.ratings?.commentsCount} ${pluralRatingCount(ratings.value?.commentsCount)}`
    : "Нет отзывов",
);
const scoreCount = computed(() =>
  ratings.value?.scoresCount
    ? `${props.product.ratings?.scoresCount} ${pluralScoreCount(ratings.value?.scoresCount)}`
    : "",
);
const reviewText = computed(() => {
  if (!scoreCount.value) {
    return "Нет отзывов";
  }

  return `${scoreCount.value} ${commentsCount.value}`;
});
const ratingScoreClass = computed(() => {
  if (!ratings.value?.rating) {
    return "";
  }
  const highScore = 4;
  return ratings.value.rating >= highScore
    ? "rating-score-high"
    : "rating-score-low";
});
const onClickRating = () => {
  const { product, categoryName, categoryId } = props;
  emit(
    "analytics",
    "CardDetail:Rating:Click",
    collectReviewPayload(product, categoryName, categoryId ?? ""),
  );
};
</script>

<template>
  <div class="product-rating" @click="onClickRating">
    <VIcon v-if="theme === 'base'">
      <StarIcon />
    </VIcon>
    <VText
      v-if="ratings && ratings.rating"
      class="product-rating-score"
      :class="ratingScoreClass"
      font="body-large-accent-low"
      color="primary"
    >
      {{ ratings.rating }}
    </VText>
    <VText
      class="product-rating-count"
      font="caption-large-regular"
      color="secondary"
      tag="div"
    >
      {{ reviewText }}
      <VIcon v-if="scoreCount" color="primary">
        <ArrowIcon />
      </VIcon>
    </VText>
  </div>
</template>

<style scoped lang="postcss">
.product-rating {
  display: flex;
  align-items: center;
  column-gap: var(--pl-unit-x2);
  margin-top: var(--pl-unit-x1);
  width: fit-content;
  cursor: pointer;

  &:active,
  &:hover {
    background-color: var(--pl-surface-primary-hover);
  }

  &-score {
    &.rating-score-high {
      color: var(--u-catalog-product-rating-score-color-high);
    }

    &.rating-score-low {
      color: var(--u-catalog-product-rating-score-color-low);
    }
  }

  &-count {
    display: flex;
    align-items: center;
  }
}
</style>
